import React from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AirLineLogo } from 'features/flightsExplorer/components/AirlineLogo';
import { formatDateToShowUser } from 'features/common/helpers';

interface SummaryMetricCellProps {
	label: string;
	value: string;
	valueColor?: string;
}

const SummaryMetricCell = ({ label, value, valueColor = 'text.primary' }: SummaryMetricCellProps): JSX.Element => (
	<Box
		sx={{
			bgcolor: 'grey.50',
			borderRadius: 1,
			px: 1,
			py: 0.75,
			minWidth: 0,
			border: 1,
			borderColor: 'divider',
		}}
	>
		<Typography variant="caption" color="text.secondary" display="block" noWrap>
			{label}
		</Typography>
		<Typography variant="body2" fontWeight={700} color={valueColor} noWrap title={value}>
			{value}
		</Typography>
	</Box>
);

export interface AuthorizationCardSummaryProps {
	authorizationId: number;
	authorizationCode: string;
	chargedAgainstOptionName: string;
	carrierCode?: string;
	paymentDueDate: string;
	createdBy?: string;
	ticketCount: number;
	statusChip: { label: string; color: 'default' | 'warning' | 'info' };
	isFullyRefunded: boolean;
	hasRefunds: boolean;
	totalRefundedLabel: string;
	partialRefundCaption?: string;
	/** Cobro por pasarela (dLocal): no cruza e-tickets, así que no se muestran las celdas de cruce. */
	isPaymentGateway?: boolean;
	authorizedLabel: string;
	crossedLabel: string;
	pendingCrossLabel: string;
	activeCrossed: number;
	pendingToCross: number;
	onCopyAuthId: (e: React.MouseEvent) => void;
	onCopyAuthCode: (e: React.MouseEvent) => void;
}

export const AuthorizationCardSummary = ({
	authorizationId,
	authorizationCode,
	chargedAgainstOptionName,
	carrierCode,
	paymentDueDate,
	createdBy,
	ticketCount,
	statusChip,
	isFullyRefunded,
	hasRefunds,
	totalRefundedLabel,
	partialRefundCaption,
	isPaymentGateway = false,
	authorizedLabel,
	crossedLabel,
	pendingCrossLabel,
	activeCrossed,
	pendingToCross,
	onCopyAuthId,
	onCopyAuthCode,
}: AuthorizationCardSummaryProps): JSX.Element => {
	const refundLine =
		hasRefunds && isFullyRefunded ? `Reembolso total ${totalRefundedLabel}` : partialRefundCaption ?? '\u00A0';

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'stretch',
				gap: 1.5,
				width: '100%',
				pr: 0.5,
				minWidth: 0,
			}}
		>
			{carrierCode && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						flexShrink: 0,
						pt: 0.25,
					}}
				>
					<AirLineLogo code={carrierCode} name={chargedAgainstOptionName} showName={false} />
				</Box>
			)}

			<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
				<Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
					<Box sx={{ minWidth: 0 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
							<Typography variant="subtitle2" fontWeight="bold" noWrap>
								Autorización #{authorizationId}
							</Typography>
							<Tooltip title="Copiar ID">
								<IconButton size="small" onClick={onCopyAuthId} sx={{ p: 0.25 }} aria-label="Copiar ID">
									<ContentCopyIcon sx={{ fontSize: 14 }} />
								</IconButton>
							</Tooltip>
							<Chip
								label={statusChip.label}
								size="small"
								color={statusChip.color}
								variant={isFullyRefunded ? 'outlined' : 'filled'}
								sx={{ height: 22, maxWidth: '100%' }}
							/>
						</Box>
						<Typography
							variant="caption"
							color="text.secondary"
							display="block"
							noWrap
							title={chargedAgainstOptionName}
						>
							{chargedAgainstOptionName}
						</Typography>
					</Box>
				</Box>

				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: isPaymentGateway
							? { xs: '1fr 2fr', sm: '1fr 2fr' }
							: { xs: '1fr 1fr 1fr', sm: '1fr 1fr 1fr' },
						gap: 0.75,
					}}
				>
					<SummaryMetricCell label="Autorizado" value={authorizedLabel} />
					{isPaymentGateway ? (
						<SummaryMetricCell label="Cruce de e-tickets" value="No aplica" />
					) : (
						<>
							<SummaryMetricCell
								label="Cruzado"
								value={crossedLabel}
								valueColor={activeCrossed > 0 ? 'success.main' : 'text.primary'}
							/>
							<SummaryMetricCell
								label="Sin cruzar"
								value={pendingCrossLabel}
								valueColor={pendingToCross > 0 ? 'warning.main' : 'text.primary'}
							/>
						</>
					)}
				</Box>

				<Typography
					variant="caption"
					color="text.secondary"
					noWrap
					sx={{ display: 'block' }}
					title={`Cód. ${authorizationCode} · Vence ${formatDateToShowUser(paymentDueDate)}`}
				>
					Cód. {authorizationCode}
					<Tooltip title="Copiar código">
						<IconButton
							size="small"
							onClick={onCopyAuthCode}
							sx={{ p: 0, ml: 0.25, verticalAlign: 'middle' }}
							aria-label="Copiar código"
						>
							<ContentCopyIcon sx={{ fontSize: 12 }} />
						</IconButton>
					</Tooltip>
					· Vence {formatDateToShowUser(paymentDueDate)}
					{createdBy ? ` · ${createdBy}` : ''}
					{ticketCount > 0 ? ` · ${ticketCount} boleto${ticketCount === 1 ? '' : 's'}` : ''}
				</Typography>

				<Typography
					variant="caption"
					color={hasRefunds && !isFullyRefunded ? 'warning.dark' : 'text.secondary'}
					sx={{
						display: 'block',
						minHeight: 18,
						lineHeight: 1.4,
						visibility: hasRefunds ? 'visible' : 'hidden',
					}}
					noWrap
				>
					{refundLine}
				</Typography>
			</Box>
		</Box>
	);
};
